import { useMemo } from 'react';
import type {
  DocumentDetail,
  DocumentPageListItem,
  DocumentReconstructedDocumentsListItem,
} from '@avisoma/shared';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { ClassificationConfidenceHistogram } from '../../components/ClassificationConfidenceHistogram';
import { ClassificationConfidenceScatter } from '../../components/ClassificationConfidenceScatter';
import { ClassificationPerformanceCard } from '../../components/ClassificationPerformanceCard';
import {
  DocumentClassificationDetails,
  getWinningClassificationSummary,
} from '../../components/DocumentClassificationDetails';
import { DocumentMetadataDetails } from '../../components/DocumentMetadataDetails';
import { DocumentModelUsageSummary } from '../../components/DocumentModelUsageSummary';
import { DocumentPagesClassificationChart } from '../../components/DocumentPagesClassificationChart';
import {
  selectRepresentativeClassificationMetrics,
  type ClassificationPageMetric,
} from '../../lib/classificationVisuals';
import { resolveClassificationRows } from '../../lib/classificationCategoryResolver';
import { useCategoryCatalogue } from '../../hooks/useCategoryCatalogue';
import { HeavyVisualGate } from './HeavyVisualGate';

interface ClassificationStatsPanelProps {
  document: DocumentDetail;
  pages: DocumentPageListItem[] | undefined;
  classificationMetrics: ClassificationPageMetric[];
  /** Gate all classification visuals by their visible per-page metric workload. */
  isHugeClassificationVisual: boolean;
  reconstructedDocuments: DocumentReconstructedDocumentsListItem[];
  reconstructionVisualsLoading: boolean;
  reconstructionVisualsError?: Error;
  onRequestReconstructionVisuals: () => void;
  onRetryReconstructionVisuals: () => void;
  onPageNumberClick: (pageNumber: number) => void;
  onChartPageClick: (page: { id: string }) => void;
}

export function ClassificationStatsPanel({
  document,
  pages,
  classificationMetrics,
  isHugeClassificationVisual,
  reconstructedDocuments,
  reconstructionVisualsLoading,
  reconstructionVisualsError,
  onRequestReconstructionVisuals,
  onRetryReconstructionVisuals,
  onPageNumberClick,
  onChartPageClick,
}: ClassificationStatsPanelProps) {
  const classificationResult = document.processing?.classification?.result;
  // DR-3784: the collapsed header must name the same category the expanded table shows, so it
  // reads a RESOLVED ranking rather than the raw stored one.
  const catalogue = useCategoryCatalogue();
  const classificationRows = useMemo(
    () => resolveClassificationRows(classificationResult, catalogue.document),
    [classificationResult, catalogue.document],
  );
  const classificationCollapsibleSummary = getWinningClassificationSummary(classificationRows);
  const representativeClassificationMetrics = useMemo(
    () => selectRepresentativeClassificationMetrics(classificationMetrics),
    [classificationMetrics],
  );
  const isClassificationSampled = representativeClassificationMetrics.length < classificationMetrics.length;
  const samplingDisclosure = isClassificationSampled ? (
    <p className="text-xs text-gray-500 dark:text-zinc-400">
      Showing {representativeClassificationMetrics.length.toLocaleString()} representative pages out of{' '}
      {classificationMetrics.length.toLocaleString()}; confidence and margin extremes retained.
    </p>
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <DocumentModelUsageSummary
          segmentationUsage={document.processing?.segmentation?.aggregatedUsageMetadata}
          pdfParserUsage={document.processing?.pdfParser?.aggregatedUsageMetadata}
          dataExtractionUsage={document.processing?.dataExtraction?.aggregatedUsageMetadata}
          dataExtractionFlashPreviewUsage={document.processing?.dataExtraction?.aggregatedFlashPreviewUsageMetadata}
          embeddingsUsage={document.processing?.embeddings?.aggregatedUsageMetadata}
          htmlConversionCostUsd={document.processing?.dataExtraction?.aggregatedHtmlConversionCostUsd}
          pageCount={document.processing?.pages?.uniquePageCount ?? 0}
        />

        <DocumentMetadataDetails metadata={document.processing?.metadata?.result} />

        {classificationCollapsibleSummary ? (
          <CollapsibleCard
            storageKey="documentView.collapsible.documentClassification"
            title="Document classification"
            summary={classificationCollapsibleSummary}
          >
            <DocumentClassificationDetails classification={classificationResult} scope="document" />
          </CollapsibleCard>
        ) : (
          <DocumentClassificationDetails classification={classificationResult} scope="document" />
        )}
      </div>

      {classificationMetrics.length > 0 && (
        <>
          <CollapsibleCard
            storageKey="documentView.collapsible.performanceCard"
            title="Classification performance"
            summary={`${classificationMetrics.length} pages`}
          >
            <ClassificationPerformanceCard pages={classificationMetrics} />
          </CollapsibleCard>

          <CollapsibleCard
            storageKey="documentView.collapsible.confidenceHistogram"
            title="Confidence histogram"
          >
            <HeavyVisualGate isHuge={isHugeClassificationVisual} label="the confidence histogram">
              <div className="space-y-2">
                <ClassificationConfidenceHistogram
                  pages={classificationMetrics}
                  representativePages={representativeClassificationMetrics}
                  onPageClick={onPageNumberClick}
                />
                {samplingDisclosure}
              </div>
            </HeavyVisualGate>
          </CollapsibleCard>

          <CollapsibleCard
            storageKey="documentView.collapsible.confidenceScatter"
            title="Confidence & margin scatter"
          >
            <HeavyVisualGate isHuge={isHugeClassificationVisual} label="the confidence & margin scatter">
              <div className="space-y-2">
                <ClassificationConfidenceScatter
                  pages={representativeClassificationMetrics}
                  pageCount={classificationMetrics.length}
                  onPageClick={onPageNumberClick}
                />
                {samplingDisclosure}
              </div>
            </HeavyVisualGate>
          </CollapsibleCard>
        </>
      )}

      <CollapsibleCard storageKey="documentView.collapsible.classificationChart" title="Classification chart">
        <div className="w-full min-h-[300px]">
          <HeavyVisualGate
            isHuge={isHugeClassificationVisual}
            label="the classification chart"
            loading={reconstructionVisualsLoading}
            error={reconstructionVisualsError}
            onRequestRender={onRequestReconstructionVisuals}
            onRetry={onRetryReconstructionVisuals}
          >
            <DocumentPagesClassificationChart
              pages={pages?.map((item) => ({
                id: item.id,
                classification: item.processing?.classification?.result ?? [],
              }))}
              reconstructedDocuments={reconstructedDocuments}
              onPageClick={onChartPageClick}
            />
          </HeavyVisualGate>
        </div>
      </CollapsibleCard>
    </div>
  );
}
